import { Injectable, inject, signal } from '@angular/core';
import { SmartAlertsApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/smart-alerts/smart-alerts-api';
import { UserAlertResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/smart-alerts/user-alert.response';
import { UserAlertStatus } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/user-alert-status.enum';
import { DataStore } from './data-store.service';

/** Alertes non lues que la carte du jour peut montrer. */
const UNREAD_PAGE_SIZE = 10;

/**
 * Les alertes non lues, une seule source pour la cloche et la carte du jour.
 *
 * Deux écrans qui comptaient chacun de leur côté finissaient par ne plus dire
 * la même chose (cloche 5, bandeau 3, DEV-04). Les lectures passent par le
 * `DataStore` : une alerte lue invalide `alerts/`, et le compteur se relit.
 */
@Injectable({ providedIn: 'root' })
export class AlertsStore {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme `PremiumService`.
  private readonly api = new SmartAlertsApi();
  private readonly data = inject(DataStore);

  /** Nombre d'alertes non lues, affiché sur la cloche. */
  readonly unreadCount = signal(0);

  /** Relit le compteur. Best-effort : en panne, la cloche garde son dernier chiffre. */
  async refreshCount(): Promise<void> {
    try {
      const result = await this.data.get('alerts/unread-count', () => this.api.getUnreadCount(), { persist: true });
      this.unreadCount.set(result?.count ?? 0);
    } catch (e) {
      console.warn('AlertsStore: failed to load the unread count', e);
    }
  }

  /** Les dernières alertes non lues (la carte du jour garde celles qui demandent un geste) ; gardées pour le hors ligne. */
  async unread(): Promise<UserAlertResponse[]> {
    return this.data.get(
      'alerts/unread',
      async () => {
        const page = await this.api.list({ status: UserAlertStatus.UNREAD }, 1, UNREAD_PAGE_SIZE);
        return page?.data ?? [];
      },
      { persist: true },
    );
  }

  /** Marque une alerte lue ; la cloche suit sans attendre un nouvel écran. */
  async markRead(uid: string): Promise<void> {
    await this.api.markAsRead(uid);
    await this.refreshCount();
  }
}
